<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <div class="list-group">
          <label v-if="title">{{ title }}</label>
          <draggable v-if="selectedItems && selectedItems.length" group="items" v-model="selectedItems" :draggable="draggingEnabled ? '.list-group-item': '.no-dragging-here'" :delay="100" :delayOnTouchOnly="true" :style="listMaxHeight ? { maxHeight: listMaxHeight, overflowY: 'auto' } : {}">
            <span v-for="item in selectedItems" class="list-group-item" :key="item.id">
              <h4 class="list-group-item-heading">
                <span class="fa fa-fw fa-close pull-right remove-draggable-item" v-on:click="removeItem(item)"></span>
                <span v-if="allowMultiple" class="pull-right">
                  <span class="fa fa-fw fa-minus" v-on:click="subtract(item)"></span>
                  {{ item.count }}
                  <span class="fa fa-fw fa-plus" v-on:click="add(item)"></span>
                </span>
                {{ itemTitle(item) }}
                <template v-if="parseInt(item.archived)">(<i class="fa fa-fw fa-archive"></i> {{ _('Archived') }})</template>
              </h4>
              <p v-if="subtitleProperty && item[subtitleProperty]" class="list-group-item-text"><small>{{ item[subtitleProperty] }}</small></p>
            </span>
          </draggable>
          <p v-else-if="noItemsText">{{ noItemsText }}</p>
        </div>
        <div class="form-group" v-if="groups || selectableItems.length">
          <label v-if="selectorTitle">{{ selectorTitle }}</label>
          <select class="form-control" v-model="selectedItem" v-if="groups">
            <optgroup v-for="group in items" :label="itemTitle(group)">
              <option v-for="item in selectableGroupItems(group)" :value="item">{{ itemTitle(item) }}</option>
            </optgroup>
          </select>
          <model-list-select v-else-if="search" :list="filteredItems" option-value="id" :custom-text="itemTitle" v-model="selectedId" @searchchange="searchItems" :placeholder="_('Type to search items')">
          </model-list-select>
          <select class="form-control" v-model="selectedItem" v-else>
            <option v-for="item in selectableItems" :value="item">{{ itemTitle(item) }}</option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { mapGetters } from 'vuex'
import { ModelListSelect } from 'vue-search-select'

export default {
  data () {
    return {
      selectedId: null,
      selectedItem: null,
      selectedItems: this.modelValue ? [...this.modelValue] : [],
      filteredItems: [],
      externalUpdate: false
    }
  },
  props: {
    modelValue: null,
    items: null,
    title: null,
    noItemsText: null,
    selectorTitle: null,
    itemProperty: null,
    groups: null,
    allowMultiple: null,
    search: null,
    displayId: null,
    displaySourceId: null,
    subtitleProperty: null,
    listMaxHeight: null,
    draggingEnabled: {
      type: Boolean,
      required: false,
      default: true
    },
    sort: {
      type: Boolean,
      required: false,
      default: false
    },
    singleSelect: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    ...mapGetters(['shopId']),
    selectableItems () {
      if (!this.selectedItems) {
        return []
      }
      if (this.allowMultiple || this.singleSelect) {
        return this.items
      }
      const selectedIds = this.selectedItems.map(item => String(item.id))
      return this.items.filter(item => selectedIds.indexOf(String(item.id)) === -1)
    }
  },
  methods: {
    searchItems(text) {
      if (!text) {
        this.filteredItems = this.selectableItems
        return
      }
      this.filteredItems = this.selectableItems.filter(p => p.name && p.name.toLowerCase().includes(text.toLowerCase()))
    },
    sortItems() {
      if(this.sort) {
        let $this = this
        this.selectedItems.sort((a,b) => ($this.itemTitle(a) > $this.itemTitle(b)) ? 1 : (($this.itemTitle(b) > $this.itemTitle(a)) ? -1 : 0))
      }
    },
    normalizeSelectedItems(items) {
      if (!this.singleSelect || !items || !items.length) {
        return items ? [...items] : []
      }
      return [items[0]]
    },
    async addItem(newValue) {
      if (this.singleSelect) {
        const existingIdx = this.selectedItems.findIndex(i => String(i.id) === String(newValue.id))
        if (existingIdx !== -1) {
          this.selectedItems = []
        } else {
          this.selectedItems = [newValue]
        }
        this.sortItems()
        await nextTick()
        this.selectedItem = null
        return
      }
      if (this.allowMultiple) {
        newValue.count = 1
        const idx = this.selectedItems.findIndex(i => i.id === newValue.id)
        if (idx !== -1) {
          let item = Object.assign({}, this.selectedItems[idx])
          item.count++
          this.selectedItems[idx] = item
        } else {
          this.selectedItems.push(newValue)
        }
      } else {
        this.selectedItems.push(newValue)
      }
      this.sortItems()
      await nextTick()
      this.selectedItem = null
    },
    add (item) {
      const idx = this.selectedItems.findIndex(i => i.id === item.id)
      if (idx !== -1) {
        item.count++
        this.selectedItems[idx] = item
        this.sortItems()
      }
    },
    subtract (item) {
      const idx = this.selectedItems.findIndex(i => i.id === item.id)
      if (idx !== -1) {
        item.count--
        if (item.count === 0) {
          this.removeItem(item)
        } else {
          this.selectedItems[idx] = item
        }
      }
    },
    removeItem (item) {
      this.selectedItems = this.selectedItems.filter(i => i.id !== item.id)
      this.sortItems()
    },
    itemTitle (item) {
      let ret = ''
      if (this.displayId && item.id) {
        ret += item.id + ': '
      }
      if (this.displaySourceId && item.source_id) {
        ret += item.source_id + ': '
      }
      if (this.itemProperty && item[this.itemProperty]) {
        ret += item[this.itemProperty]
      } else {
        ret += item.name
      }
      ret += (item.shopid && item.shopid !== this.shopId ? ' (' + item.shopid + ') ' : '')
      if (this.subtitleProperty && item[this.subtitleProperty]) {
        ret += ' — ' + item[this.subtitleProperty]
      }
      return ret
    },
    selectableGroupItems (group) {
      for (let i = 0; i < this.items.length; i++) {
        if (this.items[i].id == group.id) {
          if (this.singleSelect) {
            return group.children || []
          }
          const selectedIds = this.selectedItems.map(item => String(item.id))
          return group.children.filter(item => selectedIds.indexOf(String(item.id)) === -1)
        }
      }
      return []
    }
  },
  watch: {
    selectedId (newValue) {
      if (newValue) {
        this.selectedItem = this.selectableItems.find(i => i.id == newValue)
        this.selectedId = null
      }
    },
    selectedItem (newValue) {
      if (newValue) {
        this.addItem(newValue)
      }
    },
    selectedItems: {
      deep: true,
      handler (newValue) {
        if (!this.externalUpdate) {
          this.$emit('update:modelValue', newValue)
        }
      }
    },
    async modelValue (newValue) {
      // This is a bit ugly: we allow external changes to the value prop to affect our current state, but in order to avoid an infinite loop in the selectedItems watcher, we use a custom 'externalUpdate' variable instead
      this.externalUpdate = true
      this.selectedItems = this.normalizeSelectedItems(newValue)
      await nextTick()
      this.externalUpdate = false
    }
  },
  components: {
    ModelListSelect
  },
  mounted () {
    if (this.singleSelect && this.selectedItems.length > 1) {
      this.selectedItems = [this.selectedItems[0]]
    }
    if (this.search) {
      this.searchItems()
    }
  }
}

</script>

<style scoped>
.remove-draggable-item:hover {
  cursor: pointer;
}
</style>